import { cloneDeep } from 'lodash';

class Cache {
  // eslint-disable-next-line space-before-function-paren
  constructor() {
    this.data = {};
  }

  clear (key = 'ALL') {
    if (key !== 'ALL') {
      delete this.data[key];
    } else {
      this.data = {};
    }
  }

  set (key, value, ttl) {
    if (ttl) {
      this.data[key] = { value, expire: Date.now() + (ttl * 1000) };
    }
  }

  has (key) {
    const cache = this.data[key];

    return cache && cache.expire >= Date.now();
  }

  get (key, ifUndefined) {
    const cache = this.data[key];

    if (cache && cache.expire >= Date.now()) {
      return cache.value;
    } else {
      this.clear(key);
      if (ifUndefined && typeof ifUndefined === 'function') {
        return ifUndefined();
      } else {
        return undefined;
      }
    }
  }
}

function cachedPromise (cache, key, params = {}, ttl, getPromise) {
  let cacheKey = key;
  if (typeof params !== 'object') {
    params = { v: params };
  }
  for (const k in params) {
    cacheKey += `_${k}=${params[k]}`;
  }

  if (cache.has(cacheKey)) {
    const cacheValue = cache.get(cacheKey);
    if (cacheValue.promise) {
      // console.debug('return cached promise for ' + cacheKey);
      return cacheValue.promise;
    }
    if (cacheValue.response) {
      // console.debug('return cached response for ' + cacheKey);
      return Promise.resolve(...cacheValue.response);
    }
  } else {
    const promise = getPromise().then((...response) => {
      // console.debug('cache response for ' + cacheKey);
      cache.set(cacheKey, { response }, ttl);
      return Promise.resolve(...response);
    }).catch(err => {
      // console.debug('clear cache for ' + cacheKey);
      cache.clear(cacheKey);
      throw err;
    });

    // console.debug('cache promise for ' + cacheKey);
    cache.set(cacheKey, { promise }, Math.min(60, ttl));

    return promise;
  }
}

function storageSave (storageType, key, value) {
  if (storageType === 'SESSION') {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } else if (storageType === 'LOCAL') {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
}

function storageGet (storageType, key, defaultValue) {
  let value = null;
  if (storageType === 'SESSION') {
    value = JSON.parse(window.sessionStorage.getItem(key));
  } else if (storageType === 'LOCAL') {
    value = JSON.parse(window.localStorage.getItem(key));
  }
  return value !== null ? value : defaultValue;
}

function storageRemove (storageType, key) {
  if (storageType === 'SESSION') {
    window.sessionStorage.removeItem(key);
  } else if (storageType === 'LOCAL') {
    window.localStorage.removeItem(key);
  }
}

/**
 * @exemple
 *  usage :
 *
 *     const builder = new StoreBuilder('domain');
 *     builder.addStatePart('dataList', [], (arg1) => api.getData(arg1), 'SESSION');
 *     export default builder.build();
 *     // generate store with storage in sessionStorage :
 *     {
 *       name: 'domain'
 *       state: {
 *         dataList: []
 *       },
 *       mutations: {
 *         SET_DATALIST: f(),
 *         CLEAR_DATALIST: f(),
 *         RESET_DATALIST: f(),
 *         STORE_DATALIST: f() // store part (in sessionStorage here)
 *       },
 *       getters: {
 *         dataList: f()
 *       },
 *       actions: {
 *         setDataList: f(value)
 *         loadDataList: f(arg1) // call (arg1) => api.getData(arg1), store value in state and return it
 *         fetchDataList : alias loadDataList
 *         resetDataList : f() // reset to initial value : []
 *         clearDataList : f() // set to null
 *         reset: f()
 *       }
 *     }
 */
export default class StoreBuilder {
  /**
   * @see {StoreBuilder}
   */
  // eslint-disable-next-line space-before-function-paren
  constructor(name) {
    this.name = name;
    this.stateParts = [];
    this.mutations = {};
    this.getters = {};
    this.actions = {};
  }

  /**
   *
   * @param {String} key add a state property with 'key' name
   * @param {Any} initialValue inittial value of state part (if reset is called, state part return to initial value)
   * @param {Function} loadFunction generate an action named 'loadKey', store resuiilt in state
   * @param {String} saveStrategy 'NONE'|'SESSION'|'LOCAL' : default 'NONE', define storage strategy : no storage, sessionStorage, localStorage
   * @Param {Integer} cacheTime default:0, loadfuncxtion cache validity in seconds
   *
   * @description
   * add a state part 'key' with :
   *  - mutations :
   *    - SET_KEY : set state.key
   *    - CLEAR_KEY : set state.key to null
   *    - STORE_KEY : store value in storage (if storage strategy defined)
   *    - RESET_KEY : set state.key to initial value
   *  - getter :
   *    - key
   *  - actions
   *    - setKey
   *    - clearKey : set to null
   *    - resetKey : reset to inital value
   *    - loadKey : if loadFunction provided
   *    - fetchKey : alias loadkey
   *
   * a generic actions 'reset' is also generated
   *    - reset : reset all states parts to initials values
   */
  addStatePart (key, initialValue, loadFunction = null, saveStrategy = 'NONE', cacheTime = 0) {
    this.stateParts.push({ key, initialValue, saveStrategy, loadFunction, cacheTime });
  }

  /**
   * add mutations to store
   * override default mutations if same names
   *
   * @param {Object} mutations
   */
  setMutations (mutations) {
    this.mutations = Object.assign(this.mutations, mutations);
  }

  /**
   * add getters to store
   * override default getters is same names
   *
   * @param {Object} getters
   */
  setGetters (getters) {
    this.getters = Object.assign(this.getters, getters);
  }

  /**
   * add actions to store
   * override default actions if same names
   *
   * @param {Object} actions
   */
  setActions (actions) {
    this.actions = Object.assign(this.actions, actions);
  }

  build () {
    const __cache = new Cache();

    const module = {
      namespaced: true,
      state: { __cache },
      mutations: {},
      getters: {},
      actions: {}
    };

    module.mutations['CLEAR_CACHE'] = (state) => {
      state.__cache.clear();
    };

    module.actions.clearCache = ({ commit }) => {
      commit('CLEAR_CACHE');
    };

    for (let i in this.stateParts) {
      const part = this.stateParts[i];
      const key = part.key;
      const storageKey = this.name + '.' + key;

      module.state[key] = storageGet(part.saveStrategy, storageKey, cloneDeep(part.initialValue));

      module.mutations['SET_' + key.toUpperCase()] = (state, value) => {
        state[key] = value;
        storageSave(part.saveStrategy, storageKey, value);
      };
      module.mutations['CLEAR_' + key.toUpperCase()] = (state) => {
        state[key] = null;
        storageRemove(part.saveStrategy, storageKey);
      };
      module.mutations['STORE_' + key.toUpperCase()] = (state) => {
        storageSave(part.saveStrategy, storageKey, state[key]);
      };
      module.mutations['RESET_' + key.toUpperCase()] = (state) => {
        state[key] = cloneDeep(part.initialValue);
        storageSave(part.saveStrategy, storageKey, state[key]);
      };

      module.getters[key] = state => state[key];

      const clearKey = 'clear' + key.replace(/^(.)/, l => l.toUpperCase());
      module.actions[clearKey] = ({ commit }, value) => { commit('CLEAR_' + key.toUpperCase(), value); };

      const resetKey = 'reset' + key.replace(/^(.)/, l => l.toUpperCase());
      module.actions[resetKey] = ({ commit }, value) => { commit('RESET_' + key.toUpperCase(), value); };

      const setterKey = 'set' + key.replace(/^(.)/, l => l.toUpperCase());
      module.actions[setterKey] = ({ commit }, value) => { commit('SET_' + key.toUpperCase(), value); };

      if (part.loadFunction) {
        const loaderKey = 'load' + key.replace(/^(.)/, l => l.toUpperCase());
        const loaderKey2 = 'fetch' + key.replace(/^(.)/, l => l.toUpperCase());
        module.actions[loaderKey] = ({ commit }, params) => {
          return cachedPromise(__cache, key, params, part.cacheTime, () => part.loadFunction(params))
            .then(value => {
              commit('SET_' + key.toUpperCase(), value);
              return value;
            });
        };
        module.actions[loaderKey2] = module.actions[loaderKey];
      }
    }

    module.actions.reset = ({ commit }) => {
      for (let key in module.mutations) {
        if (key.startsWith('RESET_')) {
          commit(key);
        }
      }
    };

    module.mutations = Object.assign(module.mutations, this.mutations);
    module.getters = Object.assign(module.getters, this.getters);
    module.actions = Object.assign(module.actions, this.actions);

    return module;
  }
}
